import { RatingType, StateType } from '@/types/annotation'

/* eslint-disable camelcase */
export interface PageRequestParams {
  page: number
  pageSize: number
  totalNum: number
}

export interface PageParams extends PageRequestParams {
  totalNum: number
}

export type SearchParams = Partial<Pick<PageParams, 'page' | 'pageSize'>>

export const DEFAULT_PAGE_PARAMS = {
  page: 1,
  pageSize: 15,
  totalNum: 0,
}

export interface RequestResult<RequestData> extends PageParams {
  list: RequestData
}

export interface IChannels {
  Apps: Array<string>
  Terminal: Array<string>
  Category: Array<string>
}

export interface IManualReviewSource {
  domain?: string
  url?: string
  page_date?: string | null
}

export interface IManualReviewFinding {
  id: string
  sop_ref: string
  title: string
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info'
  category: string
  evidence: string
  location: {
    field?: string
    paragraph?: number
  }
  is_reject_trigger: boolean
  verified_value?: string | null
  sources?: IManualReviewSource[]
}

export interface IManualReviewFlowResult {
  version: string
  audited_at: string
  early_stop: boolean
  article: {
    title: string
    zh_summary: string
  }
  findings_by_category: Record<string, IManualReviewFinding[]>
  redline_summary: {
    triggered: string[]
    passed: string[]
    not_in_scope: string[]
  }
  summary: {
    total_findings: number
    redline_triggered: string[]
    by_severity: Partial<Record<IManualReviewFinding['severity'], number>>
    by_sop_ref: Record<string, number>
  }
  verified_list: Array<{
    claim_text: string
    article_value: number | string | null
    api_value: number | string | null
    deviation: string
    tool: string
  }>
  manual_check_list: Array<{
    priority: 'P0' | 'P1'
    text: string
    reason: string
  }>
  human_review_required: boolean
}

// 标注数据
export interface IAnnotation {
  id: string
  date: string
  question: string //用户问句
  answer: string // Aime回答
  rating?: RatingType // 评价
  annotator?: string // 标注者id
  annotationTime: string
  state: 'UNANNOTATED' | 'ANNOTATED' | 'SUBMITTED' //状态，未标注（UNANNOTATED），已标注（ANNOTATED），已提交（SUBMITTED）
}

export interface IPublishPage extends IChannels {
  id: string
  title: string
  content: string
  coverImage: string
  editorName?: string
  reviewerName?: string
  annotation?: IAnnotation
  origContent?: string // 初始内容,如果文章是用于标注的，则有
  prompt: string // 模型的问句入参
  reference: string // 文章引用来源
  video: boolean // 是否有视频
  machineReviewOpinion?: {
    issues?: {
      summary: string
      description: string
    }[]
  }[]
  extensions?: {
    cardInfos?: Array<string>
    characterAuthorName?: string
    manualReviewFlowResult?: IManualReviewFlowResult | string | null
  }
  status?: MaterialStatus
  scheduleTime?: number
}

//  发布文章
export interface IPublishArticle extends IChannels {
  id: string
  title: string
  content: string
  coverImage: string
  socialAccount: string // 端内社区作者账号id
}

export interface IPageIfm extends Pick<IPublishPage, 'id' | 'title'> {
  ctime: number
  rtime: number
}

export interface StockInfo {
  code: string
  name: string
  market_code: string
}

export interface ImageIdList {
  status_code: string
  staus_msg: string
  data: {
    material_id_list: Array<string>
  }
}

// 草稿列表查询参数 - 根据接口文档更新
export interface DraftListQueryParams {
  page?: number
  size?: number // 根据接口文档，使用 tab 参数
  keyword?: string
  source?: string
  startTime?: number
  endTime?: number
  status?: string // 状态筛选：draft(未发布/编辑中), scheduled(定时发布), published(已发布)
}

// 素材列表查询参数 - 根据接口文档更新
export interface MaterialListQueryParams {
  page?: number
  size?: number
  tab?: 'pending' | 'claimed' | 'completed' // 根据接口文档，使用 tab 参数
  keyword?: string
  source?: string
  startTime?: number
  endTime?: number
  editorId?: string
}

export interface IDataBaseEditParam {
  operation: 'DELETE' | 'ADDITION'
  traceId: string
  title?: string
  content: string
  source: string
}

// occ的平台标注接口入参
export interface IOCCAnnotationParam {
  scene: string //业务场景
  prompt: string //模型的问句入参
  result: string //用户的使用结果
  operator: string //运营人员名称
  modelResultList: {
    result: string //模型的结果
  }[]
}

// 数据标注入参
export interface IAnnotatioBodyParams {
  rating?: RatingType
  state: StateType
  prompt?: string
  answer?: string
  modifiedAnswer?: string
  modelName?: string
  modelVersion?: string
}

// 根据大改还是小改来区分模型信息
export const AnnotationModelInfo = {
  aime: {
    modelName: 'aime',
    modelVersion: 'oversea',
  },
  aiWriter: {
    modelName: 'wordsmith',
    modelVersion: 'v2',
  },
}

export interface IReviewPageContent {
  id: string
  materialId: string
  source: string
  title: string
  content: string
  editorId: string
  editorName: string
  reviewerId: string
  reviewerName: string
  status?: MaterialStatus
}

// 枚举当前更新操作的动作
export enum ActionType {
  reviewed = 'reviewed',
  published = 'published',
  drop = 'drop',
  abandon = 'abandon',
  save = 'save',
}

export interface IUpdateBodyParams {
  action: ActionType
  id: string
  title: string
  content: string
}

// ---------- 素材列表相关类型定义 ----------

/**
 * 素材状态枚举
 */
export type MaterialStatus = 'pending_review' | 'in_review' | 'reviewed' | 'published' | 'abandoned'

/**
 * 素材列表项
 */
export interface MaterialListItem {
  /** 素材系统内部ID */
  id: string
  /** 外部素材ID */
  materialId: string
  /** 来源系统标识 */
  source: string
  /** 素材标题 */
  title: string
  /** 内容预览 */
  content: string
  /** 素材状态 */
  status: MaterialStatus
  /** 编辑人ID */
  editorId: string
  /** 编辑人姓名 */
  editorName: string
  /** 审核人ID */
  reviewerId: string
  /** 审核人姓名 */
  reviewerName: string
  /** 创建时间戳（秒级） */
  createTime: number
  /** 更新时间戳（秒级） */
  updateTime: number
}

/**
 * 分页素材列表数据
 */
export interface PageMaterialList {
  /** 素材列表数据 */
  list: MaterialListItem[]
  /** 总条数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页条数 */
  size: number
}

/**
 * 素材列表 API 响应
 */
export interface MaterialListResponse {
  /** 业务状态码 */
  statusCode: number
  /** 业务消息 */
  statusMsg: string
  /** 响应数据 */
  list: MaterialListItem[]
  total: number
}
